import React, { useEffect, useCallback, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { signIn, signUp, autoSignIn } from "aws-amplify/auth";
import { isValidEmail, isValidPassword, toE164Kr } from "../helpers/utils";
import { LOG_IN_COOKIE, REST_API_URL, SSO_COOKIE } from "../constants";
import { Marketing, Privacy, TermsOfUse } from "../helpers/policies";
import { modal } from "../modal/ModalService";
import { useLang } from "../context/LangProvider";
import LanguageDropdown from "../helpers/LanguageDropdown";
import API from "../service/API";
import CountryDropDown from "../components/CountryDropDown";
import { deleteCookie, getCookie } from "../helpers/cookie";
import { ko } from "date-fns/locale";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import logoImage from "../img/granhand_logo.png";

registerLocale("ko", ko);

const BENEFITS = [
  {
    key: "coupon",
    label: "신규 가입 축하 쿠폰 5,000원",
  },
  {
    key: "ship",
    label: "그랑핸드 회원은 언제나 무료 배송",
  },
];

const terms = [
  {
    key: "tos",
    title: "서비스 이용 약관 동의",
    required: true,
    body: <TermsOfUse />,
  },
  {
    key: "privacy",
    title: "개인정보 수집 및 이용 동의",
    required: true,
    body: <Privacy />,
  },
  {
    key: "marketing",
    title: "개인정보 수집 및 이용 및 이벤트/마케팅 수신 동의",
    required: false,
    body: <Marketing />,
  },
];

const terms_nobody = [
  {
    key: "age",
    title: "만 14세 이상입니다.",
    required: true,
    body: "", // 본문 없음(체크만)
    noBody: true,
  },
];

const StepRender = React.memo(function StepRender({
  step,
  onNext,
  checked,
  toggle,
  allOk,
  setAll,
  requiredOk,
  email,
  setEmail,
  birth,
  setBirth,
  password,
  onAuthroize,
  setPassword,
  rePassword,
  setRepassword,
  onSignin,
  existing,
  i18n,
  phone,
  setPhone,
  phoneAuthCode,
  setPhoneAuthCode,
  onConfrimPhoneAuthCode,
  onSendPhoneAuthCode,
  lang,
  onCountrySelect,
  countryCode,
  veifyCodeSent,
  setCodeSent,
  isSending,
  onForeignerChange,
  foreigner,
  isLoading,
}) {
  switch (step) {
      case 1:
        return (
          <section className="signup-auth__step">
            <div className="signup-auth__group">
              {terms.map((t) => (
                <div className="signup-auth__option" key={t.key}>
                  <label className="signup-auth__checkbox">
                    <input
                      type="checkbox"
                      checked={checked[t.key]}
                      onChange={() => toggle(t.key)}
                    />
                    <span>{i18n(`signup.terms.${t.key}.title`)}</span>
                  </label>
                  <div className="signup-auth__panel">{t.body}</div>
                </div>
              ))}
            </div>

            <div className="signup-auth__group signup-auth__group--compact">
              {terms_nobody.map((t) => (
                <label className="signup-auth__checkbox" key={t.key}>
                  <input
                    type="checkbox"
                    checked={checked[t.key]}
                    onChange={() => toggle(t.key)}
                  />
                  <span>{i18n(`signup.terms.${t.key}.title`)}</span>
                </label>
              ))}

              <label className="signup-auth__checkbox">
                <input
                  type="checkbox"
                  checked={allOk}
                  onChange={(e) => setAll(e.target.checked)}
                />
                <span>
                  <strong>GRANHAND</strong> {i18n("signup.terms.agreeAll")}
                </span>
              </label>
            </div>

            <button
              type="button"
              onClick={onNext}
              className="signup-auth__submit"
              disabled={!requiredOk}
            >
              {i18n("signup.terms.next")}
            </button>
          </section>
        );

    case 2:
      return (
        <section className="signup-auth__step">
          <div className="signup-auth__intro">
            <p className="signup-auth__intro-title">
              {i18n("signup.step2.subtitle")}
            </p>
            <p className="signup-auth__intro-step">(1/3)</p>
          </div>

          <form className="signup-auth__form">
            <div className="signup-auth__field">
              <label className="signup-auth__label" htmlFor="email">
                {i18n("signup.form.email.label")}
              </label>
              <input
                id="email"
                type="email"
                className="signup-auth__input"
                placeholder={i18n("signup.form.email.placeholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>

            <div className="signup-auth__field">
              <label className="signup-auth__label" htmlFor="birth">
                {i18n("signup.form.birth.label")}
              </label>
              <DatePicker
                id="birth"
                className="signup-auth__input"
                selected={birth ? new Date(birth) : null}
                locale="ko"
                placeholderText={i18n("signup.form.birth.placeholder")}
                onChange={(date) => {
                  if (!date) {
                    setBirth("");
                    return;
                  }
                  const yyyy = date.getFullYear();
                  const mm = String(date.getMonth() + 1).padStart(2, "0");
                  const dd = String(date.getDate()).padStart(2, "0");
                  setBirth(`${yyyy}-${mm}-${dd}`);
                }}
                minDate={new Date(1900, 0, 1)}
                maxDate={new Date()}
                openToDate={new Date(1990, 0, 1)}
                dateFormat="yyyy-MM-dd"
                showYearDropdown
                showMonthDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={120}
                onChangeRaw={(e) => e.preventDefault()}
                onKeyDown={(e) => e.preventDefault()}
                withPortal
                portalId="birth-date-portal"
                onFocus={(e) => e.target.blur()}
                renderCustomHeader={({
                  date,
                  decreaseMonth,
                  increaseMonth,
                  decreaseYear,
                  increaseYear,
                }) => {
                  const year = date.getFullYear();
                  const month = date.getMonth() + 1;

                  return (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "4px 8px",
                      }}
                    >
                      <button type="button" onClick={decreaseYear}>
                        «
                      </button>
                      <button type="button" onClick={decreaseMonth}>
                        ‹
                      </button>
                      <span>
                        {year}년 {month}월
                      </span>
                      <button type="button" onClick={increaseMonth}>
                        ›
                      </button>
                      <button type="button" onClick={increaseYear}>
                        »
                      </button>
                    </div>
                  );
                }}
              />
            </div>

            <button
              type="button"
              onClick={onNext}
              disabled={!isValidEmail(email) || !birth}
              aria-disabled={!isValidEmail(email) || !birth}
              className="signup-auth__submit"
            >
               {i18n("common.next")}
            </button>
          </form>
        </section>
      );

    case 3:
      return (
        <section className="signup-auth__step">
          <div className="signup-auth__intro signup-auth__intro--top">
            <div className="signup-auth__intro-copy">
              <p className="signup-auth__intro-title">
                {i18n("signup.step3.subtitle")}
              </p>
              <p className="signup-auth__intro-desc">
                {i18n("signup.step3.description")}
              </p>
            </div>
            <p className="signup-auth__intro-step">(2/3)</p>
          </div>

          <form className="signup-auth__form">
            <div className="signup-auth__field">
              <label className="signup-auth__label" htmlFor="password">
                {i18n("signup.form.password.label")}
              </label>
              <input
                id="password"
                type="password"
                className="signup-auth__input"
                placeholder={i18n("signup.form.password.placeholder")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>

            <div className="signup-auth__field">
              <label className="signup-auth__label" htmlFor="rePassword">
                {i18n("signup.form.passwordConfirm.label")}
              </label>
              <input
                id="rePassword"
                type="password"
                className="signup-auth__input"
                placeholder={i18n("signup.form.passwordConfirm.placeholder")}
                value={rePassword}
                onChange={(e) => setRepassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>

            <button
              type="button"
              onClick={onNext}
              disabled={!isValidPassword(password) || password !== rePassword}
              aria-disabled={
                !isValidPassword(password) || password !== rePassword
              }
              className="signup-auth__submit"
            >
               {i18n("common.next")}
            </button>
          </form>
        </section>
      );

    case 4:
      return lang === "ko" ? (
        <section className="signup-auth__step">
          <div className="signup-auth__intro signup-auth__intro--top">
            <div className="signup-auth__intro-copy">
              <p className="signup-auth__intro-title">
                {i18n("signup.step4.title")}
              </p>
              <p className="signup-auth__intro-desc">
                {i18n("signup.step4.desc")}
              </p>
            </div>
            <p className="signup-auth__intro-step">(3/3)</p>
          </div>
          <button
            type="button"
            onClick={onAuthroize}
            className="signup-auth__outline-button"
          >
            {i18n("signup.step4.cta")}
          </button>
        </section>
      ) : (
        <div className="signup-2">
          <div className="progress-bar">
            <div
              className="progress"
              style={{
                width: "33.33%",
                position: "absolute",
                top: 0,
                left: "66.66%",
              }}
            />
          </div>
          <div className="flex-column" style={{ gap: "15px" }}>
            <div className="h4 bold">
              {i18n("signup.foreigner.verifyPhone")}
            </div>
            <CountryDropDown onCountrySelect={onCountrySelect} />
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              {countryCode ? `+${countryCode}` : null}
              <input
                id="phone"
                type="text"
                pattern="[0-9]*"
                className="login__input"
                style={{ flex: 1 }}
                placeholder={i18n("signup.foreigner.phonePlaceholder")}
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  setCodeSent(false);
                }}
              />
            </div>
            <button
              type="button"
              onClick={onSendPhoneAuthCode}
              disabled={!phone || isSending}
              aria-disabled={!phone || isSending}
              className="login__btn login__btn--primary"
            >
              {i18n("signup.foreigner.requestCode")}
            </button>
          </div>
          <div
            className="flex-column"
            style={{ gap: "15px", marginTop: "50px" }}
          >
            <div>
              <div className="login__label h6">
                {i18n("signup.foreigner.codeLabel")}
              </div>
              <input
                id="phoneAuthCode"
                type="text"
                className="login__input"
                placeholder={i18n("signup.foreigner.codePlaceholder")}
                value={phoneAuthCode}
                onChange={(e) => setPhoneAuthCode(e.target.value)}
              />
            </div>
            {veifyCodeSent ? (
              <form className="login__form" style={{ marginTop: "30px" }}>
                <div className="login__label h6" htmlFor="email">
                  {i18n("signup.foreigner.nameLabel")}
                </div>
                <input
                  id="name"
                  type="name"
                  className="login__input"
                  placeholder={i18n("signup.foreigner.namePlaceholder")}
                  value={foreigner.name}
                  onChange={(e) => onForeignerChange(e.target.value, "name")}
                />
                <div className="login__label h6" htmlFor="birth">
                  {i18n("signup.foreigner.genderLabel")}
                </div>
                <select
                  onChange={(e) => onForeignerChange(e.target.value, "gender")}
                  id="gender"
                  name="gender"
                  className="login__dropdown"
                  value={foreigner.gender}
                >
                  <option value="" disabled>
                    {i18n("signup.foreigner.genderPlaceholder")}
                  </option>
                  <option value="male">
                    {i18n("signup.foreigner.gender.male")}
                  </option>
                  <option value="female">
                    {i18n("signup.foreigner.gender.female")}
                  </option>
                  <option value="other">
                    {i18n("signup.foreigner.gender.other")}
                  </option>
                </select>
              </form>
            ) : null}
            <button
              type="button"
              disabled={
                !phone ||
                !phoneAuthCode ||
                !veifyCodeSent ||
                isLoading ||
                !foreigner.name ||
                !foreigner.gender
              }
              aria-disabled={
                !phone ||
                !phoneAuthCode ||
                !veifyCodeSent ||
                isLoading ||
                !foreigner.name ||
                !foreigner.gender
              }
              onClick={onConfrimPhoneAuthCode}
              className="login__btn login__btn--primary"
            >
              {i18n("signup.cta.submit")}
            </button>
          </div>
        </div>
      );

    case 5:
      return existing ? (
        <section className="signup-auth__step">
          <div className="signup-auth__summary">
            <p className="signup-auth__summary-title">
              {i18n("signup.existing.title")}
            </p>
            <p className="signup-auth__summary-desc">
              {i18n("signup.existing.desc.1")}
              <br />
              {i18n("signup.existing.desc.2")}
            </p>
          </div>
          <button
            type="button"
            onClick={onSignin}
            className="signup-auth__outline-button"
          >
            {i18n("signin.cta.login")}
          </button>
        </section>
      ) : (
        <section className="signup-auth__step">
          <div className="signup-auth__summary">
            <p className="signup-auth__summary-title">
              {i18n("signup.complete.title")}
            </p>
            <div className="signup-auth__benefits">
            {BENEFITS.map((b) => (
                <div key={b.key} className="signup-auth__benefit">
                <img
                  alt={i18n("signup.benefit.iconAlt")}
                  src={require(`../img/icon/${b.key}.png`)}
                    className="signup-auth__benefit-icon"
                />
                  <span>{i18n(`signup.benefit.${b.key}`)}</span>
                </div>
            ))}
            </div>
          </div>

          <button
            type="button"
            onClick={onSignin}
            className="signup-auth__outline-button"
          >
            {i18n("signup.complete.backToShop")}
          </button>
        </section>
      );
    default:
      return null;
  }
});

const TopBar = ({ onBack, i18n, step }) => {
  return (
    <header
      className="topbar"
      style={{
        maxWidth: step === 1 ? "740px" : "380px",
        margin: "0 auto",
        padding: step === 1 ? "0 16px" : "0 10px",
      }}
    >
      <div className="topbar__inner">
        <button
          type="button"
          className="topbar__back"
          onClick={onBack}
          style={{ marginBottom: 0 }}
          aria-label={i18n("signin.nav.back.aria")}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M15 18l-6-6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="topbar__back-text h6">
            {step === 1 ? i18n("signin.nav.back") : i18n("common.prevStep")}
          </div>
        </button>
        {step === 1 ? <LanguageDropdown /> : null}
      </div>
    </header>
  );
};

function SignupShell({ onBack, i18n, step, children }) {
  const backLabel =
    step === 1 ? i18n("signin.nav.back") : i18n("common.prevStep");

  return (
    <main className="signup-auth container">
      <div className="signup-auth__frame">
        <div className="signup-auth__toolbar">
          <button
            type="button"
            className="signup-auth__back"
            onClick={onBack}
            aria-label={i18n("signin.nav.back.aria")}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M15 18l-6-6 6-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{backLabel}</span>
          </button>
          <div className="signup-auth__lang">
            {step === 1 ? <LanguageDropdown /> : null}
          </div>
        </div>

        <div className="signup-auth__brand">
          <img alt="GRANHAND." src={logoImage} />
        </div>

        <section className="signup-auth__content">{children}</section>
      </div>
    </main>
  );
}

function SignupStepScreen({
  step,
  lang,
  onBack,
  i18n,
  children,
}) {
  const useSignupShell =
    step <= 3 || (step === 4 && lang === "ko") || step === 5;

  if (useSignupShell) {
    return (
      <SignupShell onBack={onBack} step={step} i18n={i18n}>
        {children}
      </SignupShell>
    );
  }

  return (
    <main className="signup">
      <TopBar onBack={onBack} step={step} i18n={i18n} />
      {children}
    </main>
  );
}

function getLangFromSsoCookie() {
  try {
    const continueUrl = getCookie(SSO_COOKIE);
    if (!continueUrl) return null;
    const redirectUri = new URL(continueUrl).searchParams.get("redirect_uri");
    if (!redirectUri) return null;
    const parts = new URL(redirectUri).hostname.split(".");
    return parts.length >= 3 ? parts[0] : null;
  } catch {
    return null;
  }
}

export default function SignupPage() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const { i18n, lang } = useLang();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [birth, setBirth] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneAuthCode, setPhoneAuthCode] = useState("");
  const [password, setPassword] = useState("");
  const [rePassword, setRepassword] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [isSending, setSending] = useState(false);
  const [veifyCodeSent, setCodeSent] = useState(false);
  const [checked, setChecked] = useState(() =>
    Object.fromEntries(terms.map((t) => [t.key, false]))
  );
  const [existing, setExisting] = useState(null);
  const [foreigner, setForeigner] = useState({});
  const [isLoading, setLoading] = useState(false);

  const doSignUp = useCallback(
    async (data) => {
      try {
        const language = getLangFromSsoCookie();
        const attrs = {
          phone_number: toE164Kr(data.phone),
          name: data.name,
          birthdate: data?.birth || birth,
          gender: data.gender || null,
          "custom:createdAt": new Date().toISOString(),
          "custom:marketing": checked.marketing ? "true" : "false",
          ...(language ? { "custom:language": language } : {}),
        };
        setLoading(true);
        const rst = await signUp({
          username: email.trim(),
          password,
          options: { userAttributes: attrs, autoSignIn: true },
        });
        if (
          rst.isSignUpComplete &&
          rst.nextStep?.signUpStep === "COMPLETE_AUTO_SIGN_IN"
        ) {
          try {
            await autoSignIn();
          } catch (e) {
            try {
              await signIn({ username: email.trim(), password });
            } catch (err) {
              console.log("Failed to siginin", err);
            }
          }
        }
        setStep(5);
        setLoading(false);
      } catch (err) {
        if (err.code === "UsernameExistsException") {
          setExisting(true);
        } else {
          modal.alert({
            message: i18n("signup.error.fail"),
            okText: i18n("common.ok"),
            backdropClose: true,
          });
        }
      }
    },
    [birth, checked.marketing, email, password, i18n]
  );

  const doSignUpRef = useRef(doSignUp);
  useEffect(() => {
    doSignUpRef.current = doSignUp;
  }, [doSignUp]);

  const requiredOk = [...terms, ...terms_nobody]
    .filter((t) => t.required)
    .every((t) => checked[t.key]);
  const allOk = [...terms, ...terms_nobody].every((t) => checked[t.key]);
  const toggle = (key) =>
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));

  const setAll = (val) =>
    setChecked(
      Object.fromEntries([...terms, ...terms_nobody].map((t) => [t.key, val]))
    );

  const handleBackPress = () => {
    if (step === 1) {
      nav("signin");
    } else {
      setStep(step - 1);
      setCodeSent(false);
    }
  };

  const handleSignin = async () => {
    deleteCookie(LOG_IN_COOKIE);
    await refresh();
    nav("/");
  };

  const handleConfrmPhoneAuthCode = async () => {
    try {
      const isValid = await API.confirmPhoneAuthCode(
        phoneAuthCode,
        phone,
        countryCode
      );
      if (isValid) {
        doForeignerSignup();
      } else {
        modal.alert({
          message: i18n("signup.foreigner.incorrectCode"),
          okText: i18n("common.ok"),
          backdropClose: true,
        });
      }
    } catch (err) {
      modal.alert({
        message: i18n("common.tryAgain"),
        okText: i18n("common.ok"),
        backdropClose: true,
      });
    }
  };

  const doForeignerSignup = async () => {
    try {
      const language = getLangFromSsoCookie();
      const attrs = {
        phone_number: `+${countryCode}${phone}`,
        name: foreigner.name,
        birthdate: birth,
        gender: foreigner.gender || null,
        "custom:createdAt": new Date().toISOString(),
        "custom:marketing": checked.marketing ? "true" : "false",
        ...(language ? { "custom:language": language } : {}),
      };

      const rst = await signUp({
        username: email.trim(),
        password,
        options: { userAttributes: attrs, autoSignIn: true },
      });
      if (
        rst.isSignUpComplete &&
        rst.nextStep?.signUpStep === "COMPLETE_AUTO_SIGN_IN"
      ) {
        try {
          await autoSignIn();
        } catch (e) {
          try {
            await signIn({ username: email.trim(), password });
          } catch (err) {
            console.log("Failed to siginin", err);
          }
        }
      }
      setStep(5);
    } catch (err) {
      if (err.code === "UsernameExistsException") {
        setExisting(true);
      } else {
        modal.alert({
          message: i18n("signup.error.fail"),
          okText: i18n("common.ok"),
          backdropClose: true,
        });
      }
    }
  };

  const handleForeignerChange = (value, type) => {
    setForeigner({ ...foreigner, [type]: value });
  };

  const handleSendPhoneAuthCode = async () => {
    try {
      setSending(true);
      await API.sendPhoneAuthCode(phone, countryCode);
      setCodeSent(true);
      setSending(false);
    } catch (err) {
      setSending(false);
    }
  };

  const handleNext = () => {
    setStep(step + 1);
  };

  const handleAuthorize = async () => {
    window.MOBILEOK?.process(`${REST_API_URL}/mok/request`, "WB", "result");
  };

  const handleCountrySelect = (country) => {
    setCountryCode(country.dialCode);
  };

  useEffect(() => {
    window.result = async function (payload) {
      fetch(
        `${REST_API_URL}/mok/result?txId=${encodeURIComponent(
          JSON.parse(payload).txId
        )}`
      )
        .then((r) => r.json())
        .then((json) => {
          doSignUpRef.current(json);
        });
    };

    return () => {
      delete window.result;
    };
  }, []);

  return (
    <SignupStepScreen
      step={step}
      lang={lang}
      onBack={handleBackPress}
      i18n={i18n}
    >
      <StepRender
        step={step}
        onNext={handleNext}
        checked={checked}
        toggle={toggle}
        allOk={allOk}
        setAll={setAll}
        requiredOk={requiredOk}
        email={email}
        setEmail={setEmail}
        birth={birth}
        setBirth={setBirth}
        password={password}
        setPassword={setPassword}
        rePassword={rePassword}
        setRepassword={setRepassword}
        onAuthroize={handleAuthorize}
        onSignin={handleSignin}
        existing={existing}
        i18n={i18n}
        phone={phone}
        setPhone={setPhone}
        phoneAuthCode={phoneAuthCode}
        setPhoneAuthCode={setPhoneAuthCode}
        onSendPhoneAuthCode={handleSendPhoneAuthCode}
        onConfrimPhoneAuthCode={handleConfrmPhoneAuthCode}
        lang={lang}
        onCountrySelect={handleCountrySelect}
        countryCode={countryCode}
        veifyCodeSent={veifyCodeSent}
        setCodeSent={setCodeSent}
        isSending={isSending}
        onForeignerChange={handleForeignerChange}
        foreigner={foreigner}
        isLoading={isLoading}
      />
    </SignupStepScreen>
  );
}
